import React, { useState } from "react"
import Modal from "react-bootstrap/Modal"
import Form from "react-bootstrap/Form"
import Alert from "react-bootstrap/Alert"
import { generateReceiptPdf } from "../../../Utils.jsx"
import { fetchSignatureBlob } from "@stjeans/shared-frontend/utils"

/**
 * Lodge-specific confirm flow. Confirmation emails are sent by OMS after sync, not by Lodge.
 *
 * @param {{
 *   Button: any,
 *   app: any,
 *   api: any,
 *   helpers: any,
 *   order: any,
 *   refreshOrder: () => Promise<void>,
 *   onError: (error: any) => void,
 *   disableConfirmWhenOffline?: boolean,
 * }} props
 */
export default function LodgeOrderConfirmActionsV1({
    Button,
    app,
    api,
    helpers,
    order,
    refreshOrder,
    onError,
    disableConfirmWhenOffline = false,
}) {
    const [showModal, setShowModal] = useState(false)
    const [modalPreset, setModalPreset] = useState(/** @type {"confirm" | null} */(null))
    const [staffInitial, setStaffInitial] = useState("")

    function showToast(message) {
        onError?.(new Error(message))
    }

    function handleCloseModal() {
        setShowModal(false)
        setModalPreset(null)
    }

    function onStaffInitialInput(event) {
        const val = event.currentTarget.value
        setStaffInitial(String(val ?? "").toUpperCase())
    }

    function showConfirmModal() {
        if (!app?.connected) {
            showToast("Cannot confirm orders while offline")
            return
        }

        if (staffInitial) {
            setStaffInitial("")
        }
        setModalPreset("confirm")
        setShowModal(true)
    }

    async function getPDF() {
        // @ts-ignore - Vite injects `import.meta.env` at build time
        const signatureUrl = order?.signature_img
            ? `${import.meta.env.VITE_STATIC_PREFIX}${order.id}/${order.signature_img}`
            : null
        const signatureBlob = await fetchSignatureBlob(signatureUrl, {
            orderId: order?.id,
            context: "LodgeOrderConfirmActionsV1.getPDF",
        })
        return generateReceiptPdf(
            order,
            order?.master,
            app?.locations ?? [],
            signatureBlob,
            app?.species ?? [],
            app?.options ?? [],
            app?.weighInProcesses ?? []
        )
    }

    function uploadReceiptFile() {
        const formatTimeDashes = helpers?.formatTimeDashes
        return getPDF()
            .then((pdfBytes) => {
                const file = new Blob([pdfBytes], { type: "application/pdf" })
                return api.uploadFile(order.id, file, `email-receipt-${formatTimeDashes(new Date())}.pdf`)
            })
    }

    function onConfirmClick() {
        api.confirmOrder(order.id, staffInitial)
            .then(() => {
                return uploadReceiptFile().catch((e) => {
                    console.error(e)
                })
            })
            .then(() => refreshOrder())
            .catch((e) => {
                console.error(e)
                onError?.(e)
            })

        handleCloseModal()
    }

    const status = String(order?.status ?? "").toLowerCase()
    const isUnconfirmed = status === "" || status === "new"
    const isConfirmed = status === "confirmed"
    const confirmDisabledBecauseOffline = Boolean(disableConfirmWhenOffline && app?.connected === false)

    if (!order?.id || (!isUnconfirmed && !isConfirmed)) {
        return null
    }

    const validateConfirmOrder = helpers?.validateConfirmOrder
    const [invalid, missingFields] =
        typeof validateConfirmOrder === "function" ? validateConfirmOrder(order) : [false, []]

    return (
        <>
            {isUnconfirmed ? (
                confirmDisabledBecauseOffline ? (
                    <span className="d-inline-block" title="Cannot confirm orders while offline.">
                        <Button variant="outline" size="sm" disabled>
                            Confirm Order
                        </Button>
                    </span>
                ) : (
                    <Button variant="outline" size="sm" onClick={showConfirmModal}>
                        Confirm Order
                    </Button>
                )
            ) : null}

            {modalPreset === "confirm" ? (
                <Modal show={showModal} onHide={handleCloseModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Order</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            By confirming this order, you verify that the order details have been reviewed and are correct. You will not be able to edit the order once confirmed.
                        </p>
                        <p>
                            The customer will receive a confirmation email from St Jean's within the next hour.
                        </p>
                        <p>Confirmed orders are automatically added to a manifest.</p>
                        <Form.Group className="mb-3 d-flex align-items-end">
                            <Form.Label>
                                Staff Initial:<span className="text-danger">&nbsp;*</span>
                            </Form.Label>
                            <Form.Control
                                className="ms-3"
                                value={staffInitial}
                                onInput={onStaffInitialInput}
                                style={{ width: "100px" }}
                                required
                            />
                        </Form.Group>
                        {invalid ? (
                            <Alert variant="danger">
                                {`Order is missing fields: ${(missingFields ?? []).join(", ")}`}
                            </Alert>
                        ) : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={onConfirmClick}
                            disabled={!staffInitial.trim() || Boolean(invalid)}
                        >
                            Confirm
                        </Button>
                    </Modal.Footer>
                </Modal>
            ) : null}
        </>
    )
}
